import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, Trash2, Plus, Play, Pause, RefreshCw, AudioLines, Pencil, Check, X, ShieldCheck, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { formatDate } from "../lib/constants";
import { useMicRecorder } from "../hooks/useMicRecorder";
import { ConfirmDialog } from "../components/ConfirmDialog";

const MIN_SEC = 8;

export default function MyPailot() {
  const [voices, setVoices] = useState([]);
  const [frase, setFrase] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ nome: "", ruolo: "" });
  const [saving, setSaving] = useState(false);
  const [action, setAction] = useState(null); // {mode:'enroll'|'reenroll'|'test', voiceId}
  const [testResult, setTestResult] = useState(null); // {voiceId, data}
  const [renaming, setRenaming] = useState(null); // {id, nome, ruolo}
  const [toDelete, setToDelete] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [cloning, setCloning] = useState(null); // voiceId in corso di clonazione
  const rec = useMicRecorder();
  const audioRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/mypailot/voices");
      setVoices(data.items);
      setFrase(data.frase);
      setIsAdmin(!!data.is_admin);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => audioRef.current?.pause();
  }, [load]);

  const beginEnroll = async () => {
    if (!form.nome.trim()) {
      toast.error("Inserisci prima un nome per la voce");
      return;
    }
    setTestResult(null);
    setAction({ mode: "enroll" });
    await rec.start();
  };

  const beginReenroll = async (voiceId) => {
    setTestResult(null);
    setAction({ mode: "reenroll", voiceId });
    await rec.start();
  };

  const beginTest = async (voiceId) => {
    setTestResult(null);
    setAction({ mode: "test", voiceId });
    await rec.start();
  };

  const cancelRec = () => {
    rec.stop();
    setAction(null);
  };

  const finishRec = async () => {
    const pcm = rec.stop();
    const mode = action?.mode;
    const voiceId = action?.voiceId;
    if ((mode === "enroll" || mode === "reenroll") && rec.seconds < MIN_SEC) {
      toast.error(`Leggi la frase per intero: servono almeno ${MIN_SEC} secondi di voce.`);
      setAction(null);
      return;
    }
    setSaving(true);
    setAction(null);
    try {
      if (mode === "enroll") {
        const qs = `nome=${encodeURIComponent(form.nome.trim())}&ruolo=${encodeURIComponent(form.ruolo.trim())}`;
        await api.post(`/mypailot/voices?${qs}`, pcm, { headers: { "Content-Type": "application/octet-stream" } });
        toast.success("Voce registrata");
        setForm({ nome: "", ruolo: "" });
        setAdding(false);
        await load();
      } else if (mode === "reenroll") {
        await api.post(`/mypailot/voices/${voiceId}/audio`, pcm, { headers: { "Content-Type": "application/octet-stream" } });
        toast.success("Voce aggiornata");
        await load();
      } else if (mode === "test") {
        const { data } = await api.post(`/mypailot/voices/${voiceId}/test`, pcm, { headers: { "Content-Type": "application/octet-stream" } });
        setTestResult({ voiceId, data });
      }
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const playAudio = async (voiceId) => {
    if (playingId === voiceId) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    try {
      audioRef.current?.pause();
      const { data } = await api.get(`/mypailot/voices/${voiceId}/audio`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => setPlayingId(null);
      await a.play();
      setPlayingId(voiceId);
    } catch (e) {
      toast.error("Audio non disponibile");
    }
  };

  const saveRename = async () => {
    try {
      await api.put(`/mypailot/voices/${renaming.id}`, { nome: renaming.nome, ruolo: renaming.ruolo });
      setRenaming(null);
      toast.success("Aggiornato");
      await load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/mypailot/voices/${toDelete.id}`);
      setToDelete(null);
      toast.success("Voce eliminata");
      await load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const setAsAgentVoice = async (v) => {
    setCloning(v.id);
    try {
      await api.post(`/mypailot/voices/${v.id}/agent`);
      toast.success(`L'agente vocale ora parla con la voce di ${v.nome}`);
      await load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setCloning(null);
    }
  };

  const resetAgent = async () => {
    try {
      await api.delete("/mypailot/agent-voice");
      toast.success("L'agente vocale è tornato alla voce di default");
      await load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const isRecording = rec.recording && !!action;

  return (
    <div className="space-y-8" data-testid="mypailot-page">
      <div className="fade-up">
        <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Voci operatori</p>
        <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">My<span className="text-primary">Pailot</span></h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Registra la voce di ogni operatore del tuo team. Le voci servono all'Assistente Live per riconoscere chi parla e,
          per l'amministratore, come voce dell'agente vocale AI nelle chiamate in entrata e in uscita. Ogni voce è privata del tuo account.
        </p>
        {isAdmin && (
          <p data-testid="clone-hint" className="mt-3 max-w-2xl rounded-lg border border-[#F5B942]/30 bg-[#F5B942]/10 px-4 py-3 text-xs leading-relaxed text-[#FCD990]">
            Consiglio per una clonazione fedele: registra almeno 60-90 secondi di parlato naturale (puoi leggere la frase più volte o
            continuare a parlare liberamente) in un ambiente silenzioso, poi premi «Clona e usa per l'agente vocale».
          </p>
        )}
      </div>

      {/* Recording overlay */}
      {isRecording && (
        <div data-testid="recording-box" className="fixed inset-x-4 bottom-6 z-50 mx-auto max-w-lg rounded-2xl border border-red-500/40 bg-[#1a1114] p-5 shadow-2xl backdrop-blur">
          <p className="text-sm leading-relaxed">
            {action.mode === "test" ? "Parla liberamente per qualche secondo con il tuo tono normale…" : `Leggi ad alta voce: "${frase}"`}
          </p>
          <div className="mt-4 flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2 font-mono text-xs text-red-300">
              <span className="pulse-dot h-2 w-2 rounded-full bg-red-400" /> REC {String(Math.floor(rec.seconds / 60)).padStart(2, "0")}:{String(rec.seconds % 60).padStart(2, "0")}
            </span>
            <span className="inline-flex h-4 items-end gap-0.5">
              {[0.02, 0.05, 0.1, 0.18, 0.3].map((th, i) => (
                <span key={i} className="w-1 rounded-sm" style={{ height: `${(i + 1) * 20}%`, background: rec.level > th ? "#34D399" : "#374151" }} />
              ))}
            </span>
            <div className="flex gap-2">
              <button data-testid="rec-cancel-btn" onClick={cancelRec} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-2 text-xs font-medium text-muted-foreground hover:text-white"><X className="h-3.5 w-3.5" /> Annulla</button>
              <button data-testid="rec-stop-btn" onClick={finishRec} className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-semibold text-black"><Square className="h-3.5 w-3.5" /> {action.mode === "test" ? "Verifica" : "Salva"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add new voice */}
      <section className="fade-up">
        {!adding ? (
          <button data-testid="add-voice-btn" onClick={() => setAdding(true)} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1D4ED8]">
            <Plus className="h-4 w-4" /> Aggiungi voce operatore
          </button>
        ) : (
          <div data-testid="add-voice-form" className="surface space-y-4 p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 text-primary"><AudioLines className="h-5 w-5" /></div>
              <h2 className="text-base font-semibold md:text-lg">Nuova voce</h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Nome operatore</label>
                <input data-testid="voice-nome-input" value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} placeholder="Es. Mario Rossi" className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm outline-none focus:border-primary" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Ruolo / etichetta (opzionale)</label>
                <input data-testid="voice-ruolo-input" value={form.ruolo} onChange={(e) => setForm((f) => ({ ...f, ruolo: e.target.value }))} placeholder="Es. Senior, Closer…" className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm outline-none focus:border-primary" />
              </div>
            </div>
            <p className="rounded-lg border border-border bg-background/60 p-4 text-sm italic leading-relaxed text-foreground/90">“{frase}”</p>
            <p className="-mt-1 text-xs text-muted-foreground">Leggilo come se fossi davvero al telefono: varia il tono tra domande, entusiasmo e calma. Serve a calibrare la voce nel modo più naturale possibile.</p>
            <div className="flex flex-wrap gap-2">
              <button data-testid="start-enroll-btn" onClick={beginEnroll} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 spin-slow" /> : <Mic className="h-4 w-4" />} Leggi la frase e registra
              </button>
              <button onClick={() => { setAdding(false); setForm({ nome: "", ruolo: "" }); }} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-white">Annulla</button>
            </div>
          </div>
        )}
      </section>

      {/* Voices list */}
      <section className="fade-up">
        <h2 className="mb-4 text-base font-semibold md:text-lg">Voci registrate {voices.length > 0 && <span className="text-muted-foreground">({voices.length})</span>}</h2>
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 spin-slow text-primary" /></div>
        ) : voices.length === 0 ? (
          <div data-testid="voices-empty" className="surface p-10 text-center text-sm text-muted-foreground">Nessuna voce registrata. Aggiungi la prima qui sopra.</div>
        ) : (
          <ul data-testid="voices-list" className="grid gap-3 sm:grid-cols-2">
            {voices.map((v) => (
              <li key={v.id} data-testid={`voice-card-${v.id}`} className="surface space-y-4 p-5">
                {renaming?.id === v.id ? (
                  <div className="space-y-2">
                    <input data-testid="rename-nome-input" value={renaming.nome} onChange={(e) => setRenaming((r) => ({ ...r, nome: e.target.value }))} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
                    <input data-testid="rename-ruolo-input" value={renaming.ruolo || ""} onChange={(e) => setRenaming((r) => ({ ...r, ruolo: e.target.value }))} placeholder="Ruolo" className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
                    <div className="flex gap-2">
                      <button data-testid="rename-save-btn" onClick={saveRename} className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-white"><Check className="h-3.5 w-3.5" /> Salva</button>
                      <button onClick={() => setRenaming(null)} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground"><X className="h-3.5 w-3.5" /> Annulla</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-300"><AudioLines className="h-4 w-4" /></div>
                        <p className="truncate font-semibold">{v.nome}</p>
                        {v.voce_agente && (
                          <span data-testid={`agent-badge-${v.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#F5B942]/20 px-2 py-0.5 text-[11px] font-medium text-[#FCD990]"><PhoneCall className="h-3 w-3" /> Voce dell'agente</span>
                        )}
                      </div>
                      <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                        {v.ruolo && <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-primary">{v.ruolo}</span>}
                        <span>{formatDate(v.created_at)}</span>
                        {v.duration_sec != null && <span>· {v.duration_sec}s</span>}
                        {v.coerenza != null && <span>· coerenza {Math.round(v.coerenza * 100)}%</span>}
                      </p>
                    </div>
                    <button data-testid={`rename-${v.id}`} onClick={() => setRenaming({ id: v.id, nome: v.nome, ruolo: v.ruolo })} className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-white" title="Rinomina"><Pencil className="h-4 w-4" /></button>
                  </div>
                )}

                {testResult?.voiceId === v.id && (
                  <p data-testid={`test-result-${v.id}`} className={`rounded-lg px-3 py-2 text-sm ${testResult.data.riconosciuto ? "bg-emerald-500/15 text-emerald-200" : "bg-amber-500/15 text-amber-200"}`}>
                    {testResult.data.riconosciuto ? "Voce riconosciuta" : "Voce non riconosciuta"} · somiglianza {Math.round(testResult.data.similarita * 100)}%
                  </p>
                )}

                <div className="flex flex-wrap gap-2">
                  <button data-testid={`play-${v.id}`} onClick={() => playAudio(v.id)} disabled={!v.has_audio} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-white disabled:opacity-40">
                    {playingId === v.id ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />} {playingId === v.id ? "Pausa" : "Ascolta"}
                  </button>
                  <button data-testid={`test-${v.id}`} onClick={() => beginTest(v.id)} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-white disabled:opacity-40"><Mic className="h-3.5 w-3.5" /> Prova</button>
                  <button data-testid={`reenroll-${v.id}`} onClick={() => beginReenroll(v.id)} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-white disabled:opacity-40"><RefreshCw className="h-3.5 w-3.5" /> Registra di nuovo</button>
                  <button data-testid={`delete-${v.id}`} onClick={() => setToDelete(v)} className="inline-flex items-center gap-1.5 rounded-full border border-red-500/30 px-3 py-1.5 text-xs font-medium text-red-300 transition-colors hover:bg-red-500/15"><Trash2 className="h-3.5 w-3.5" /> Elimina</button>
                </div>

                {isAdmin && (
                  <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
                    {v.voce_agente ? (
                      <button data-testid={`agent-reset-${v.id}`} onClick={resetAgent} className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-white"><X className="h-3.5 w-3.5" /> Torna alla voce di default</button>
                    ) : (
                      <button data-testid={`agent-use-${v.id}`} onClick={() => setAsAgentVoice(v)} disabled={!v.has_audio || !!cloning} className="inline-flex items-center gap-1.5 rounded-full bg-[#F5B942]/15 px-3 py-1.5 text-xs font-semibold text-[#FCD990] transition-colors hover:bg-[#F5B942]/25 disabled:opacity-40">
                        {cloning === v.id ? <Loader2 className="h-3.5 w-3.5 spin-slow" /> : <PhoneCall className="h-3.5 w-3.5" />} {v.clonata ? "Usa per l'agente vocale (ri-clona)" : "Clona e usa per l'agente vocale"}
                      </button>
                    )}
                    {v.cloned_at && <span className="text-[11px] text-muted-foreground">clonata il {formatDate(v.cloned_at)}</span>}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="surface flex items-start gap-3 p-4 text-sm text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <p>Nell'Assistente Live, prima di avviare una call, scegli quale operatore sta parlando tra le voci qui registrate: il sistema riconoscerà la sua voce come "operatore" e l'altra come cliente.</p>
      </div>

      <ConfirmDialog open={!!toDelete} title="Eliminare questa voce?" description={`La voce «${toDelete?.nome || ""}» verrà eliminata definitivamente.`} onCancel={() => setToDelete(null)} onConfirm={doDelete} />
    </div>
  );
}
